import React, { useCallback } from 'react';
import { usePricingFormContext } from './PricingFormContext';
import { Col, Row, Layout, Checkbox, Divider, InputNumber, Button, message, Modal } from 'antd';

// 检查数值的小数位数是否超过2位
const checkDecimalPlaces = (value: number | null | undefined, fieldName: string): boolean => {
    if (value === null || value === undefined) {
        return true;
    }
    const str = value.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex === -1) {
        return true; // 没有小数部分，通过
    }
    const decimalPart = str.substring(decimalIndex + 1);
    if (decimalPart.length > 2) {
        message.warning(`${fieldName}最多支持两位小数，当前输入超过限制`);
        return false;
    }
    return true;
};

const headerStyle: React.CSSProperties = {
    color: '#090909ff',
    lineHeight: '25px',
    backgroundColor: '#fff',
    paddingLeft: '0px',
    paddingRight: '0px',
};
const dividerStyle: React.CSSProperties = {
    margin: '15px',
};

// 业务类型（TT）：CBT / FBT
export interface BizTypeFeeCard {
    bizTypes: string[];
    priceUsd?: number | null;
}

export interface BizTypeFeeData {
    cards: BizTypeFeeCard[];
}

const PriceCard: React.FC<{
    cardIndex: number;
    card: BizTypeFeeCard;
    onUpdate: (index: number, card: BizTypeFeeCard) => void;
    onDelete: () => void;
    onCopy: () => void;
    disabled?: boolean;
    bizTypeOptions?: Array<{ label: string; value: string }>;
}> = ({ cardIndex, card, onUpdate, onDelete, onCopy, disabled = false, bizTypeOptions = [] }) => {
    const { Header, Sider, Content } = Layout;
    const CheckboxGroup = Checkbox.Group;

    const handleBizTypesChange = (bizTypes: string[]) => {
        onUpdate(cardIndex, { ...card, bizTypes });
    };

    return (
        <Layout style={{ backgroundColor: '#fff', padding: 15, border: '1px solid #e8e8e8', borderRadius: 5 }}>
            <Header style={headerStyle}>
                <Row justify="center" align="middle">
                    <Col span={18}>
                        <span>业务类型</span>
                    </Col>
                    <Col span={6}>
                        <span>Price/价格</span>
                    </Col>
                </Row>
                <Divider style={dividerStyle} />
            </Header>
            <Layout>
                <Sider width="70%" theme="light">
                    <CheckboxGroup
                        options={bizTypeOptions}
                        value={card.bizTypes || []}
                        onChange={(v) => handleBizTypesChange(v as string[])}
                        disabled={disabled}
                    />
                </Sider>
                <Content style={{ background: '#fff' }}>
                    <InputNumber
                        size="small"
                        style={{ width: 70 }}
                        value={card.priceUsd ?? undefined}
                        disabled={disabled}
                        onChange={(v) => {
                            if (v !== null && v !== undefined) {
                                checkDecimalPlaces(v, '金额');
                            }
                            onUpdate(cardIndex, { ...card, priceUsd: v ?? null });
                        }}
                    />
                    {' '}美金
                </Content>
            </Layout>
            <Row justify="end" style={{ marginTop: 10 }}>
                <Col>
                    <Button type="default" size="small" onClick={onCopy} disabled={disabled} style={{ marginRight: 8 }}>复制</Button>
                    <Button type="primary" danger size="small" onClick={onDelete} disabled={disabled}>删除</Button>
                </Col>
            </Row>
        </Layout>
    );
};

const BizTypeFeeTabb: React.FC<{
    tabId: string;
    data?: BizTypeFeeData;
    onChange?: (data: BizTypeFeeData) => void;
    feeItemCodeNum?: number;
    onDataChange?: (code: number, data: BizTypeFeeData) => void;
    disabled?: boolean;
    bizTypeOptions?: Array<{ label: string; value: string }>;
}> = ({ tabId, data: dataProp, onChange, feeItemCodeNum, onDataChange, disabled = false, bizTypeOptions = [] }) => {
    const ctx = usePricingFormContext();
    const data = dataProp ?? (feeItemCodeNum != null ? ctx?.feeItemDataMap?.get(feeItemCodeNum) : undefined);
    const cards = data?.cards || [{ bizTypes: [] }];
    const notify = useCallback((payload: BizTypeFeeData) => {
        if (onDataChange != null && feeItemCodeNum != null) onDataChange(feeItemCodeNum, payload);
        else onChange?.(payload);
    }, [onDataChange, onChange, feeItemCodeNum]);

    const handleAddCard = () => {
        const newCards = [...cards, { bizTypes: [] }];
        notify({ cards: newCards });
    };

    const handleDeleteCard = (index: number) => {
        if (cards.length > 1) {
            Modal.confirm({
                title: '确认删除',
                content: '确定要删除这个价格卡片吗？删除后无法恢复。',
                okText: '确认',
                cancelText: '取消',
                okType: 'danger',
                onOk: () => {
                    const newCards = cards.filter((_: BizTypeFeeCard, i: number) => i !== index);
                    notify({ cards: newCards });
                }
            });
        }
    };

    const handleUpdateCard = (index: number, card: BizTypeFeeCard) => {
        const newCards = [...cards];
        newCards[index] = card;
        notify({ cards: newCards });
    };

    const handleCopyCard = (index: number) => {
        const cardToCopy = cards[index];
        const copiedCard: BizTypeFeeCard = {
            bizTypes: [...(cardToCopy?.bizTypes || [])],
            priceUsd: cardToCopy?.priceUsd,
        };
        const newCards = [...cards];
        newCards.splice(index + 1, 0, copiedCard);
        notify({ cards: newCards });
    };

    return (
        <div>
            {cards.map((card: BizTypeFeeCard, index: number) => (
                <div key={index} style={{ marginBottom: index !== cards.length - 1 ? 15 : 0 }}>
                    <PriceCard
                        cardIndex={index}
                        card={card}
                        onUpdate={handleUpdateCard}
                        onDelete={() => handleDeleteCard(index)}
                        onCopy={() => handleCopyCard(index)}
                        disabled={disabled}
                        bizTypeOptions={bizTypeOptions}
                    />
                </div>
            ))}
            <Row justify="end" style={{ marginTop: 15 }}>
                <Col>
                    <Button type="primary" size="middle" onClick={handleAddCard} disabled={disabled}>新增</Button>
                </Col>
            </Row>
        </div>
    );
};

export default React.memo(BizTypeFeeTabb);
